import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useRouter } from "src/hooks/use-router";
import { AptLinkButtons } from "src/components/apt-link-buttons";

// ─── 가격 포맷 ─────────────────────────────────────────────
/** 만원 단위 숫자를 억/만 표시로 변환 */
const formatPrice = (v) => {
  if (v == null || v === 0) return "-";
  if (typeof v === "string") {
    const num = parseInt(v.replace(/,/g, ""), 10);
    if (isNaN(num)) return v;
    return formatPrice(num);
  }
  if (v >= 10000) return `${(v / 10000).toFixed(1)}억`;
  return `${Math.round(v).toLocaleString()}만`;
};

/** 변동 표시 */
const PriceChange = ({ value }) => {
  if (value == null || value === 0) return null;
  const color = value > 0 ? "#E53935" : "#1565C0";
  const arrow = value > 0 ? "▲" : "▼";
  return (
    <Typography component="span" sx={{ fontSize: 10, fontWeight: 600, color, ml: 0.3 }}>
      {arrow}{formatPrice(Math.abs(value))}
    </Typography>
  );
};

/** 변동률 표시 */
const RateChange = ({ value }) => {
  if (value == null || value === 0) return null;
  const color = value > 0 ? "#E53935" : "#1565C0";
  const arrow = value > 0 ? "▲" : "▼";
  return (
    <Typography component="span" sx={{ fontSize: 10, fontWeight: 600, color, ml: 0.3 }}>
      {arrow}{Math.abs(value).toFixed(1)}%
    </Typography>
  );
};

/** 날짜를 "YY.MM.DD" 포맷 */
const formatShortDate = (dateValue) => {
  if (!dateValue) return "";
  const d = typeof dateValue === "number" ? new Date(dateValue) : new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getFullYear()).slice(2)}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
};

/** 짧은 날짜 라벨 (주간/월간 구분) */
const getDateLabels = (item) => {
  if (!item) return { prevLabel: null, curLabel: null };
  const pd = item.prevDate ? new Date(item.prevDate) : null;
  const cd = item.dataDate ? new Date(item.dataDate) : null;
  if (!cd) return { prevLabel: null, curLabel: null };
  const isWeekly = pd && Math.round((cd - pd) / (1000 * 60 * 60 * 24)) <= 14;
  if (isWeekly) {
    return {
      prevLabel: pd ? `${pd.getMonth() + 1}/${pd.getDate()}` : null,
      curLabel: `${cd.getMonth() + 1}/${cd.getDate()}`,
    };
  }
  return {
    prevLabel: pd ? `${pd.getMonth() + 1}월` : null,
    curLabel: `${cd.getMonth() + 1}월`,
  };
};

/** 날짜별 from→to 인라인 표시 (날짜 회색, 값 검정 bold) */
const StatFromToInline = ({ stat, format }) => {
  if (!stat) return null;
  const { prevLabel, curLabel } = getDateLabels(stat);
  const prev = stat.prevValue;
  const cur = stat.value;
  if (prev == null && cur == null) return null;
  const fmt = format || formatPrice;
  return (
    <Typography component="span" variant="caption" sx={{ fontSize: 10 }}>
      {prevLabel && prev != null && (
        <>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{prevLabel} </Typography>
          <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{fmt(prev)}</Typography>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}> → </Typography>
        </>
      )}
      {curLabel && cur != null && (
        <>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{curLabel} </Typography>
          <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{fmt(cur)}</Typography>
        </>
      )}
    </Typography>
  );
};

/**
 * 관심 아파트 요약 카드.
 * @param {object} apt - 아파트 데이터 (SupplyPyeongPriceDto + dealStat/leaseStat + recentDeal/recentLease)
 * @param {function} onClick - 클릭 핸들러
 */
export const AptSummaryCard = ({ apt, onClick, chart = null, fullWidth = false, hideTitle = false, showStar = true }) => {
  const router = useRouter();
  const aptId = apt.aptId;

  return (
  <Box
    onClick={onClick}
    sx={{
      // fullWidth=true: 부모 폭 100% (다이얼로그/세로 리스트용)
      // fullWidth=false: 가로 스크롤용 고정 폭
      minWidth: fullWidth ? 0 : 320,
      maxWidth: fullWidth ? "100%" : 400,
      width: fullWidth ? "100%" : undefined,
      p: 1.2,
      border: 1, borderColor: "divider", borderRadius: 1.5,
      cursor: onClick ? "pointer" : "default",
      flexShrink: fullWidth ? 1 : 0,
      "&:hover": onClick ? { bgcolor: "action.hover", borderColor: "primary.main" } : {},
      transition: "all 0.15s",
    }}
  >
    {/* 아파트명 + 새창/네이버 (인라인) + (우측) 신고가 — hideTitle=true면 숨김 (다이얼로그 헤더와 중복 방지). 지역명은 별도 노출. */}
    {!hideTitle ? (
      <>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* 이름 옆에 새창열기/네이버 버튼 인라인 배치 — 별표는 다이얼로그 내부에선 의미가 없으니 호출처에서 끌 수 있도록 showStar prop 전달 */}
            <Stack direction="row" alignItems="center" spacing={0.3} sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12, lineHeight: 1.2, minWidth: 0 }} noWrap>
                {apt.aptName} {apt.supplyPyeong}평
              </Typography>
              <AptLinkButtons aptId={aptId} pyeongId={apt.pyeongId} aptName={apt.aptName} supplyPyeong={apt.supplyPyeong} iconSize={12} onOpen={router.newWindow} showStar={showStar} sx={{ flexShrink: 0 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {apt.sidoName} {apt.gunguName} {apt.dongName}
            </Typography>
          </Box>
          {apt.isNewHigh && (
            <Box sx={{ flexShrink: 0, ml: 0.5 }}>
              <Chip label="신고가" size="small" sx={{ bgcolor: "#FFEBEE", color: "#E53935", fontWeight: 700, fontSize: 9, height: 18 }} />
            </Box>
          )}
        </Stack>
        <Divider sx={{ my: 0.5 }} />
      </>
    ) : (
      <>
        {/* hideTitle=true: 지역명만 표시 (헤더의 이름/링크와 중복 안 됨) */}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: "block" }}>
          {apt.sidoName} {apt.gunguName} {apt.dongName}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
      </>
    )}

    {/* 매매시세: 제목 + 날짜별 값 (인라인) + 변동 */}
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>매매시세</Typography>
        <StatFromToInline stat={apt.dealStat} format={formatPrice} />
      </Stack>
      {(apt.dealPriceChange != null && apt.dealPriceChange !== 0)
        ? <PriceChange value={apt.dealPriceChange} />
        : (apt.dealPriceChangeRate != null && apt.dealPriceChangeRate !== 0)
          ? <RateChange value={apt.dealPriceChangeRate} />
          : <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>-</Typography>}
    </Stack>

    {/* 전세시세: 제목 + 날짜별 값 (인라인) + 변동 */}
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.2 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>전세시세</Typography>
        <StatFromToInline stat={apt.leaseStat} format={formatPrice} />
      </Stack>
      {(apt.leasePriceChange != null && apt.leasePriceChange !== 0)
        ? <PriceChange value={apt.leasePriceChange} />
        : (apt.leasePriceChangeRate != null && apt.leasePriceChangeRate !== 0)
          ? <RateChange value={apt.leasePriceChangeRate} />
          : <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>-</Typography>}
    </Stack>

    {/* 평당가 / 전세가율 / 매전갭: 값만 검정 bold */}
    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.3 }}>
      <Typography component="span" variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>
        평당가 <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{apt.dealPricePerSpaceAvr > 0 ? `${Math.round(apt.dealPricePerSpaceAvr).toLocaleString()}만` : "-"}</Typography>
      </Typography>
      <Typography component="span" variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>
        전세가율 <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{apt.leasePriceRateAvr != null ? `${apt.leasePriceRateAvr.toFixed(1)}%` : "-"}</Typography>
      </Typography>
      <Typography component="span" variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>
        갭 <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{formatPrice(apt.dealLeaseGapAvr)}</Typography>
      </Typography>
    </Stack>

    {/* 매물수 — 평당가와 실거래 사이 */}
    <Stack direction="row" spacing={1} sx={{ mt: 0.3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>매물</Typography>
      <Typography variant="caption" sx={{ fontSize: 10, color: "#1645F0" }}>매매 {apt.dealCount ?? 0}</Typography>
      <Typography variant="caption" sx={{ fontSize: 10, color: "#FC3512" }}>전세 {apt.leaseCount ?? 0}</Typography>
    </Stack>

    {/* 최근 실거래 */}
    <Divider sx={{ my: 0.5 }} />
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, fontWeight: 600 }}>실거래</Typography>
    {/* 매매 실거래: 가격(날짜) → 가격(날짜) 증감 */}
    {apt.recentDeal ? (
      <Stack direction="row" alignItems="baseline" spacing={0.3} sx={{ flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ fontSize: 10, color: "#1645F0" }}>매매</Typography>
        {apt.prevDeal && (
          <>
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: "text.primary" }}>{formatPrice(apt.prevDeal.priceNum)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8 }}>({formatShortDate(apt.prevDeal.date)})</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>→</Typography>
          </>
        )}
        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{formatPrice(apt.recentDeal.priceNum)}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8 }}>({formatShortDate(apt.recentDeal.date)} {apt.recentDeal.floor && `${apt.recentDeal.floor}층`})</Typography>
        <PriceChange value={apt.realDealChange} />
      </Stack>
    ) : (
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="caption" sx={{ fontSize: 10, color: "#1645F0" }}>매매</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {apt.dealRealPriceAvr ? `${formatPrice(apt.dealRealPriceAvr)} (평균)` : "-"}
        </Typography>
      </Stack>
    )}
    {/* 전세 실거래: 가격(날짜) → 가격(날짜) 증감 */}
    {apt.recentLease ? (
      <Stack direction="row" alignItems="baseline" spacing={0.3} sx={{ flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ fontSize: 10, color: "#FC3512" }}>전세</Typography>
        {apt.prevLease && (
          <>
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: "text.primary" }}>{formatPrice(apt.prevLease.priceNum)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8 }}>({formatShortDate(apt.prevLease.date)})</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>→</Typography>
          </>
        )}
        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{formatPrice(apt.recentLease.priceNum)}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8 }}>({formatShortDate(apt.recentLease.date)} {apt.recentLease.floor && `${apt.recentLease.floor}층`})</Typography>
        <PriceChange value={apt.realLeaseChange} />
      </Stack>
    ) : (
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="caption" sx={{ fontSize: 10, color: "#FC3512" }}>전세</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {apt.leaseRealPriceAvr ? `${formatPrice(apt.leaseRealPriceAvr)} (평균)` : "-"}
        </Typography>
      </Stack>
    )}

    {/* 미니 차트 (실거래 + 매물시세) */}
    {chart}
  </Box>
  );
};
